import { boothLines, C, conventions, shows } from "@bd/content";
import { priceIndex, WEEKS_PER_YEAR, yearOf } from "./calendar";
import { BALANCE } from "./balance";
import { roundNice } from "./director";
import { addMod } from "./mods";
import { fanMarket } from "./sales";
import type { Company, GameState, Product, Project } from "./state";
import { clamp, digits, log, money, rng } from "./util";

/**
 * Conventions you attend: a few times a year a fair or convention takes bookings. A booth costs money and shows
 * one of your games: fans, a sales lift for a couple of months (or hype for a game still in the works), and at an
 * awards convention a small chance the judges notice. Results go in the log ("Your booth at ...").
 */

export interface Booth {
  con: string;
  /** the week the convention runs */
  week: number;
  /** product or project id */
  game: string;
  /** the game's name when booked (a project becomes a product with a new id when it's printed) */
  name: string;
  cost: number;
}

export interface ConDate {
  id: string;
  name: string;
  week: number;
  size: number;
  /** a booth there, in today's dollars */
  cost: number;
  /** hands out awards (Dicefall Con, Tabletop Expo, the Harvest Games Fair) */
  awards: boolean;
}

function conDef(id: string): { id: string; name: string; year: number; week: number; size: number; booth: number; awards: boolean } | null {
  const con = C.convention[id];
  if (con) return { id: con.id, name: con.name, year: con.year, week: con.week, size: con.size, booth: con.booth ?? 800, awards: true };
  const sh = C.show[id];
  return sh ? { ...sh, awards: false } : null;
}

/** The next time a convention runs, from this week on (the awards run in its week: week of year = its "week"). */
export function nextConWeek(id: string, week: number): number | null {
  const c = conDef(id);
  if (!c) return null;
  let year = Math.max(yearOf(week), c.year);
  let w = (year - 1965) * WEEKS_PER_YEAR + c.week;
  if (w < week) {
    year += 1;
    w = (year - 1965) * WEEKS_PER_YEAR + c.week;
  }
  return w;
}

export function boothCost(id: string, week: number): number {
  const c = conDef(id);
  return c ? Math.round(c.booth * priceIndex(week)) : 0;
}

/** Conventions and fairs in the next year, soonest first. */
export function conCalendar(state: GameState, weeksAhead = WEEKS_PER_YEAR): ConDate[] {
  const out: ConDate[] = [];
  for (const id of [...conventions.map((c) => c.id), ...shows.map((s) => s.id)]) {
    const c = conDef(id)!;
    const w = nextConWeek(id, state.week + 1);
    if (w === null || w - state.week > weeksAhead || w >= state.settings.endWeek) continue;
    out.push({ id, name: c.name, week: w, size: c.size, cost: boothCost(id, w), awards: c.awards });
  }
  return out.sort((a, b) => a.week - b.week);
}

/** Games you can show: still in the works, then on sale from the last three years (newest first), then the best-selling old evergreens. */
export function boothGames(co: Company, week: number): { id: string; name: string; launched: boolean }[] {
  const live = co.products.filter((p) => p.launched && !p.retired);
  const recent = live.filter((p) => week - p.releaseWeek < 3 * WEEKS_PER_YEAR).sort((a, b) => b.releaseWeek - a.releaseWeek);
  // older evergreens: the five that still sell best
  const classics = live.filter((p) => p.evergreen && week - p.releaseWeek >= 3 * WEEKS_PER_YEAR).sort((a, b) => (b.weekly.at(-1) ?? 0) - (a.weekly.at(-1) ?? 0) || b.score - a.score).slice(0, 5);
  const onSale = [...recent, ...classics];
  return [
    ...co.projects.map((p) => ({ id: p.id, name: p.design.name, launched: false })),
    ...co.products.filter((p) => !p.launched && !p.retired && p.pending).map((p) => ({ id: p.id, name: p.design.name, launched: false })),
    ...onSale.map((p) => ({ id: p.id, name: p.design.name, launched: true })),
  ];
}

/** The bookBooth command: book a booth at the next running of a convention for one of your games. */
export function bookBooth(state: GameState, co: Company, conId: string, game: string): string | null {
  const c = conDef(conId);
  if (!c) return "No such convention.";
  const week = nextConWeek(conId, state.week + 1);
  if (week === null || week - state.week > WEEKS_PER_YEAR || week >= state.settings.endWeek) return `${c.name} isn't taking bookings yet.`;
  if ((co.booths ?? []).some((b) => b.con === conId && b.week === week)) return `You already have a booth at ${c.name}.`;
  const g = boothGames(co, state.week).find((x) => x.id === game);
  if (!g) return "Pick one of your games to show.";
  const cost = boothCost(conId, week);
  if (co.cash < cost) return `A booth at ${c.name} costs ${money(cost)}.`;
  co.cash -= cost;
  co.deductible = (co.deductible ?? 0) + cost;
  co.booths = [...(co.booths ?? []), { con: conId, week, game, name: g.name, cost }];
  return null;
}

function findGame(co: Company, b: Booth): { prod?: Product; proj?: Project } {
  const prod = co.products.find((p) => p.id === b.game) ?? co.products.find((p) => p.design.name === b.name && !p.retired);
  if (prod && !prod.retired) return { prod };
  const proj = co.projects.find((p) => p.id === b.game) ?? co.projects.find((p) => p.design.name === b.name);
  return proj ? { proj } : {};
}

const fill = (text: string, v: Record<string, string>) => text.replace(/\{(\w+)\}/g, (m, k: string) => v[k] ?? m);

/** A booth's weekend: fans, a sales lift or hype, maybe the judges' eye. */
export function runBooth(state: GameState, co: Company, b: Booth): void {
  const c = conDef(b.con);
  if (!c) return;
  const r = rng(state, `booth:${co.id}`);
  const { prod, proj } = findGame(co, b);
  // a game still in the works: a project, or a first print run on its way
  const early = proj ?? (prod && !prod.launched ? prod.pending : undefined);
  if (!prod?.launched && !early) {
    log(state, "booth", fill(boothLines.unused, { con: c.name, game: b.name }), co.id);
    return;
  }
  const name = (prod ?? early)!.design.name;
  const crowd = r.float(0.6, 1.4) * c.size * (prod?.launched ? clamp(prod.score / 7, 0.5, 1.4) : clamp(0.7 + early!.hype, 0.7, 1.3));
  const fans = Math.max(10, roundNice(BALANCE.boothFans * crowd * (fanMarket(state.week) / 20000)));
  co.fans += fans;
  if (prod?.launched) {
    addMod(state, co, { kind: "sales", value: 1 + BALANCE.boothSales * crowd, target: `product:${prod.id}`, weeks: BALANCE.boothWeeks, label: `Booth at ${c.name}` });
    const line = crowd > 0.75 ? boothLines.busy : crowd > 0.4 ? boothLines.steady : boothLines.quiet;
    log(state, "booth", fill(line, { con: c.name, game: name, fans: digits(fans) }), co.id);
    // the judges walk the hall too: a game from the past year might catch their eye
    if (c.awards && state.week - prod.releaseWeek <= WEEKS_PER_YEAR && r.chance(BALANCE.boothNominateP * c.size)) {
      co.awardBoost += BALANCE.boothNominateBoost;
      log(state, "booth", fill(boothLines.nominated, { con: c.name, game: name }), co.id);
    }
  } else {
    early!.hype = clamp(early!.hype + BALANCE.boothHype * crowd);
    log(state, "booth", fill(crowd > 0.6 ? boothLines.projectBusy : boothLines.projectQuiet, { con: c.name, game: name, fans: digits(fans) }), co.id);
  }
}

/** Runs before the conventions hand out their awards, so a booth's goodwill counts the same week. */
export const boothSystem = {
  name: "booths",
  week(state: GameState): void {
    for (const co of state.companies) {
      if (!co.booths?.length) continue;
      const due = co.booths.filter((b) => b.week <= state.week);
      if (!due.length) continue;
      co.booths = co.booths.filter((b) => b.week > state.week);
      for (const b of due) if (b.week === state.week && !co.bankrupt) runBooth(state, co, b);
    }
  },
};
